import { Mail, Rocket } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useParent } from '../ParentContext';

interface Counts {
  signedUp: number;
  paired24h: number;
  paired: number;
  active: number;
  trial: number;
  paid: number;
  cancelled: number;
}
interface GrowthData {
  settings: { stallDays: number; activeDays: number };
  weeks: (Counts & { week: string })[];
  totals: Counts;
  stalled: { familyId: string; family: string; owner: string; email: string; signedUpAt: number; days: number; children: number }[];
}

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '–');
const weekLabel = (ymd: string) => `Week of ${new Date(`${ymd}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

/**
 * Admin → Growth (#553): how new families get on, by the week they signed up. Signed up → a computer paired within a
 * day → active in week one → on a trial → paying. And who stalled, so someone can give them a call.
 */
export function Growth() {
  const { toast } = useParent();
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState<GrowthData | null>(null);
  const [form, setForm] = useState<{ stallDays: number; activeDays: number } | null>(null);
  const load = useCallback(() => {
    api
      .get<GrowthData>(`/api/admin/growth?weeks=${weeks}`)
      .then((d) => {
        setData(d);
        setForm(d.settings);
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [weeks, toast]);
  useEffect(load, [load]);
  if (!data || !form) return <p className="muted">Loading…</p>;
  const t = data.totals;
  const dirty = form.stallDays !== data.settings.stallDays || form.activeDays !== data.settings.activeDays;
  async function save() {
    try {
      await api.put('/api/admin/growth/settings', form);
      toast({ text: 'Saved.', tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const steps: [keyof Counts, string, string][] = [
    ['signedUp', 'Signed up', 'New families'],
    ['paired24h', 'Paired in a day', 'A computer set up within 24 hours'],
    ['active', 'Active in week one', `School time on ${data.settings.activeDays}+ days in their first week`],
    ['trial', 'On a trial', 'Trialling a paid plan now'],
    ['paid', 'Paying', 'A working subscription'],
  ];
  return (
    <div className="stack growth">
      <section className="card stack">
        <div className="row-gap wrap growth-head">
          <h2 className="with-icon grow">
            <Rocket size={18} /> Launch funnel
          </h2>
          <label className="row-gap">
            <span className="muted small">Last</span>
            <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
              {[4, 8, 12, 26, 52].map((w) => (
                <option key={w} value={w}>
                  {w} weeks
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="finance-kpis growth-kpis" role="list">
          {steps.map(([k, label, note]) => (
            <div key={k} className="usage-tile" role="listitem">
              <span className="muted small">{label}</span>
              <span className="usage-value">{t[k]}</span>
              <span className="muted small">{k === 'signedUp' ? note : `${pct(t[k], t.signedUp)} · ${note}`}</span>
            </div>
          ))}
        </div>
        <div className="table-scroll">
          <table className="growth-table">
            <thead>
              <tr>
                <th>Signed up in</th>
                <th>Families</th>
                <th>Paired in a day</th>
                <th>Paired at all</th>
                <th>Active week one</th>
                <th>Trial</th>
                <th>Paying</th>
                <th>Cancelled</th>
              </tr>
            </thead>
            <tbody>
              {data.weeks.map((w) => (
                <tr key={w.week} className={w.signedUp ? '' : 'muted'}>
                  <td>{weekLabel(w.week)}</td>
                  <td>{w.signedUp}</td>
                  {(['paired24h', 'paired', 'active', 'trial', 'paid', 'cancelled'] as const).map((k) => (
                    <td key={k}>
                      {w[k]} <span className="muted small">{w.signedUp ? pct(w[k], w.signedUp) : ''}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small">Families count in the week they signed up. Active and paying are where they are now.</p>
      </section>

      <section className="card stack">
        <h2>Stalled: signed up, nothing paired after {data.settings.stallDays} days</h2>
        {!data.stalled.length ? (
          <p className="muted">Nobody’s stuck. Every family past {data.settings.stallDays} days has a computer paired.</p>
        ) : (
          <ul className="growth-stalled">
            {data.stalled.map((s) => (
              <li key={s.familyId}>
                <div className="grow">
                  <strong>{s.owner}</strong> <span className="muted small">{s.family}</span>
                  <div className="muted small">
                    Signed up {s.days} day{s.days === 1 ? '' : 's'} ago · {s.children} child{s.children === 1 ? '' : 'ren'} added
                  </div>
                </div>
                <a className="btn small" href={`mailto:${s.email}?subject=${encodeURIComponent('Getting ClassGuardian set up')}`}>
                  <Mail size={14} /> {s.email}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card stack">
        <h2>Settings</h2>
        <div className="row-gap wrap">
          <label>
            Stalled after (days)
            <input type="number" min={1} max={60} value={form.stallDays} onChange={(e) => setForm({ ...form, stallDays: Number(e.target.value) })} />
          </label>
          <label>
            Active in week one: school time on (days)
            <input type="number" min={1} max={7} value={form.activeDays} onChange={(e) => setForm({ ...form, activeDays: Number(e.target.value) })} />
          </label>
        </div>
        <div>
          <button className="btn primary" disabled={!dirty} onClick={save}>
            Save
          </button>
        </div>
      </section>
    </div>
  );
}
